import React, { useEffect, useRef, useState } from "react";
import ConnectionView from "./ConnectionView";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import BaseModelMap from "../models/BaseModelMap";
import Connection, { ConnectionType } from "../models/Connection";
import { PagePrintInfo } from "../models/PagePrintInfo";
import Logger from "../Logger";
import type Page from "../models/Page";
import { useRedraw } from "../models/useRerender";
import { RenderTarget, useRenderTarget } from "../components/renderPrimitives/renderTarget";

const logger = new Logger();

export default function ConnectionsView(props: {
  mainPage: Page;
  page: Page;
  fontScale: number;
  connections: BaseModelMap<Connection>;
  print?: PagePrintInfo;
}) {
  const {
    mainPage, // the root page opened (not the nested page of a room view)
    page,
    fontScale,
    print,
  } = props;

  const renderTarget = useRenderTarget();

  const connectionsViewRef = useRef<any>(null);
  const hideConnectionsTimer = useRef<NodeJS.Timeout | null>(null);

  const [connections, setConnections] = useState<BaseModelMap<Connection> | undefined>(undefined);

  const redraw = useRedraw();

  useEffect(() => {
    /////////////// set forbidden for already existing connections
    setTimeout(() => {
      setConnections(props.connections);
    }, 100);
  }, []);

  useEffect(() => {
    /////////////// set forbidden for already existing connections
    connections?.forEach((connection) => {
      connection.checkForbidden();
    });
  }, [connections]);

  useOnChangeWatcher(props.connections, (changedProperty, changedObject) => {
    setTimeout(() => {
      redraw();
    }, 150);
    return false;
  });

  useOnChangeWatcher(state.connectionDisplayMode);

  useOnChangeWatcher(state.uiMode, () => {
    mainPage.recalcConnections.value++;
    return false;
  });

  useOnChangeWatcher(mainPage.recalcConnections, (changedProperty, changedObject) => {
    if (connectionsViewRef.current) {
      connectionsViewRef.current.style.transition = "opacity 0.2s ease-in-out";
      connectionsViewRef.current.style.opacity = "0";

      if (hideConnectionsTimer.current) {
        clearTimeout(hideConnectionsTimer.current);
      }

      hideConnectionsTimer.current = setTimeout(() => {
        //
        // recalculate connections
        //
        page.updateConnections();
        redraw();

        if (connectionsViewRef.current) {
          connectionsViewRef.current.style.transition = "opacity 0.2s ease-in-out";
          connectionsViewRef.current.style.opacity = "1";
        }
      }, 500);
    }
    return false;
  });

  logger.log("render ConnectionsView");

  // SVG/HTML-based connections are not compatible with @react-pdf/renderer
  if (renderTarget === RenderTarget.pdf) {
    return null;
  }

  const connectionsArray = connections?.asArray();

  if (!connectionsArray) {
    return null;
  }

  if (mainPage.editRooms) {
    return null;
  }

  //
  // sort connections by connnectionType
  // this makes sure,
  // Channel-Connections are drawn on top of the rest of types
  //
  // As a result, in "non export", connection texts of channel connections
  // are on top and the white backgounds overlays the other connections
  //

  const sortedConnections = connectionsArray.sort((c1, c2) => {
    if (c1.type == c2.type) {
      return 0;
    }

    if (c1.type == ConnectionType.Channel) {
      return 1;
    } else {
      return -1;
    }
  });
  function connectionShouldBeShownHere(connection: Connection): boolean {
    const target = connection.toDevice;
    const source = connection.fromDevice;
    if (connection.type == ConnectionType.Channel) {
      return true;
    }

    return false;
  }

  const children: any = [];

  sortedConnections.forEach((connection) => {
    // check if connection should be drawn here
    console.log("fontScale A", fontScale);

    children.push(
      <ConnectionView
        key={connection.id}
        mainPage={mainPage}
        page={page}
        fontScale={fontScale}
        connection={connection}
        print={print}
      />,
    );
  });
  return (
    <div
      style={{
        pointerEvents: "none",
        position: "absolute",
        left: "0px",
        top: "0px",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: "0",
      }}
    >
      <svg
        className="connections"
        id="connections"
        ref={connectionsViewRef}
        style={{
          pointerEvents: "none",
          position: "absolute",
          left: "-500px",
          top: "-500px",
          width: "calc(100% + 1000px)",
          height: "calc(100% + 1000px)",
        }}
      >
        <defs>
          <filter x="0" y="0" width="1.2" height="1.2" id="txtBG">
            <feFlood
              floodColor="white"
              result="bg" // no itx
            />
            <feMerge>
              <feMergeNode
                in="bg" // no itx
              />
              <feMergeNode
                in="SourceGraphic" // no itx
              />
            </feMerge>
          </filter>
        </defs>
        <g
          transform="translate(500, 500)" // no itx
        >
          {children}
        </g>
      </svg>
    </div>
  );

  return (
    <svg
      className="connections"
      id="connections"
      ref={connectionsViewRef}
      style={{
        pointerEvents: "none",
        position: "absolute",
        left: "-500px",
        top: "-500px",
        width: "calc(100% + 1000px)",
        height: "calc(100% + 1000px)",
      }}
    >
      <defs>
        <filter x="0" y="0" width="1.2" height="1.2" id="txtBG">
          <feFlood
            floodColor="white"
            result="bg" // no itx
          />
          <feMerge>
            <feMergeNode
              in="bg" // no itx
            />
            <feMergeNode
              in="SourceGraphic" // no itx
            />
          </feMerge>
        </filter>
      </defs>
      <g
        transform="translate(500, 500)" // no itx
      >
        {children}
      </g>
    </svg>
  );
}
